import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';

// Material UI components
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

// Icons
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BuildIcon from '@mui/icons-material/Build';
import InventoryIcon from '@mui/icons-material/Inventory';
import QrCodeIcon from '@mui/icons-material/QrCode';

// Context
import AuthContext from '../../context/AuthContext';

// Services
import { getTechnicians, deleteTechnician } from '../../services/technicianService';
import { getWarehouses } from '../../services/warehouseService';

// Components
import TechnicianForm from '../../components/TechnicianForm';
import BundlePrintDialog from '../../components/BundlePrintDialog';

const TechniciansList = () => {
  const { user } = useContext(AuthContext);
  const [technicians, setTechnicians] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Form dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [technicianToDelete, setTechnicianToDelete] = useState(null);

  // Bundle print dialog state
  const [bundlePrintDialogOpen, setBundlePrintDialogOpen] = useState(false);
  const [selectedTechnicianForPrint, setSelectedTechnicianForPrint] = useState(null);

  // Check if user can manage technicians
  const canManage = user && ['manager', 'purchaser'].includes(user.role);

  // Fetch technicians and warehouses
  const fetchTechnicians = async () => {
    try {
      setLoading(true);
      const filters = {
        search: searchTerm,
        type: typeFilter,
        include_inactive: includeInactive && user?.role === 'manager'
      };

      const data = await getTechnicians(filters);

      // Apply warehouse filter on frontend since API doesn't support it yet
      let filteredData = data;
      if (warehouseFilter) {
        if (warehouseFilter === 'unassigned') {
          filteredData = data.filter(tech => !tech.warehouse_id);
        } else {
          filteredData = data.filter(tech => tech.warehouse_id === parseInt(warehouseFilter));
        }
      }

      setTechnicians(filteredData);
      setError(null);
    } catch (err) {
      console.error('Error fetching technicians:', err);
      setError('Failed to load technicians. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch warehouses
  const fetchWarehouses = async () => {
    try {
      const warehouseData = await getWarehouses();
      setWarehouses(Array.isArray(warehouseData) ? warehouseData : []);
    } catch (err) {
      console.error('Error fetching warehouses:', err);
      setWarehouses([]);
    }
  };

  // Load warehouses on component mount
  useEffect(() => {
    fetchWarehouses();
  }, []);

  // Load technicians on component mount and when filters change
  useEffect(() => {
    fetchTechnicians();
  }, [searchTerm, typeFilter, warehouseFilter, includeInactive]);

  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Handle type filter
  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
  };

  // Handle warehouse filter
  const handleWarehouseFilterChange = (event) => {
    setWarehouseFilter(event.target.value);
  };

  // Handle include inactive toggle
  const handleIncludeInactiveChange = (event) => {
    setIncludeInactive(event.target.checked);
  };

  // Handle form operations
  const handleOpenForm = (technician = null) => {
    setSelectedTechnician(technician);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setSelectedTechnician(null);
    setFormOpen(false);
  };

  const handleFormSuccess = () => {
    fetchTechnicians();
    toast.success(selectedTechnician ? 'Technician updated successfully' : 'Technician created successfully');
  };

  // Handle delete operations
  const handleOpenDeleteDialog = (technician) => {
    setTechnicianToDelete(technician);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setTechnicianToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteTechnician = async () => {
    try {
      await deleteTechnician(technicianToDelete.id);
      fetchTechnicians();
      handleCloseDeleteDialog();
      toast.success('Technician deactivated successfully');
    } catch (err) {
      console.error('Error deleting technician:', err);
      toast.error('Failed to deactivate technician');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Get type icon
  const getTypeIcon = (type) => {
    return type === 'technician' ? <EngineeringIcon /> : <BuildIcon />;
  };

  // Get type chip
  const getTypeChip = (type) => {
    return (
      <Chip
        icon={getTypeIcon(type)}
        label={type.charAt(0).toUpperCase() + type.slice(1)}
        color={type === 'technician' ? 'primary' : 'secondary'}
        size="small"
        variant="outlined"
      />
    );
  };

  // Handle printing QR codes for a specific technician
  const handlePrintTechnicianQR = (technician) => {
    setSelectedTechnicianForPrint(technician);
    setBundlePrintDialogOpen(true);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <EngineeringIcon sx={{ mr: 2, color: 'primary.main' }} />
          Technicians & Installers
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<InventoryIcon />}
            onClick={() => window.location.href = '/technician-inventory'}
          >
            View All Inventory
          </Button>
          {canManage && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => handleOpenForm()}
            >
              Add Technician
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: searchTerm && (
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon />
                </IconButton>
              )
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={handleTypeFilterChange}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="technician">Technician</MenuItem>
              <MenuItem value="installer">Installer</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Warehouse</InputLabel>
            <Select
              value={warehouseFilter}
              label="Warehouse"
              onChange={handleWarehouseFilterChange}
            >
              <MenuItem value="">All Warehouses</MenuItem>
              <MenuItem value="unassigned">Unassigned</MenuItem>
              {warehouses.map(warehouse => (
                <MenuItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {user?.role === 'manager' && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                id="include-inactive"
                checked={includeInactive}
                onChange={handleIncludeInactiveChange}
              />
              <label htmlFor="include-inactive" style={{ marginLeft: 8 }}>
                Include inactive
              </label>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {/* Technicians Table */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                {canManage && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 6 : 5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading technicians...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : technicians.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 6 : 5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1">
                      No technicians found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                technicians.map((technician) => (
                  <TableRow
                    key={technician.id}
                    hover
                    sx={{
                      ...(!technician.is_active && {
                        bgcolor: 'action.hover',
                        '& td': { color: 'text.disabled' }
                      })
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1,
                            bgcolor: technician.is_active ? 'primary.main' : 'grey.500'
                          }}
                        >
                          {technician.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {technician.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{getTypeChip(technician.type)}</TableCell>
                    <TableCell>
                      {technician.warehouse ? (
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {technician.warehouse.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {technician.warehouse.location}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          No warehouse assigned
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={technician.is_active ? "Active" : "Inactive"}
                        color={technician.is_active ? "success" : "default"}
                        size="small"
                        variant={technician.is_active ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>{formatDate(technician.created_at)}</TableCell>
                    {canManage && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <Tooltip title="View Inventory">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => window.location.href = `/technicians/${technician.id}/inventory`}
                            >
                              <InventoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print QR Labels">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => {
                                setSelectedTechnicianForPrint(technician);
                                setBundlePrintDialogOpen(true);
                              }}
                            >
                              <QrCodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Technician">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenForm(technician)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {technician.is_active && (
                            <Tooltip title="Deactivate Technician">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenDeleteDialog(technician)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Technician Form Dialog */}
      <TechnicianForm
        open={formOpen}
        onClose={handleCloseForm}
        technician={selectedTechnician}
        onSuccess={handleFormSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Deactivate Technician</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deactivate "{technicianToDelete?.name}"?
            This will hide them from the active list but preserve their data for historical records.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteTechnician} color="error" variant="contained">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bundle Print Dialog */}
      <BundlePrintDialog
        open={bundlePrintDialogOpen}
        onClose={() => {
          setBundlePrintDialogOpen(false);
          setSelectedTechnicianForPrint(null);
        }}
        preSelectedTechnician={selectedTechnicianForPrint}
      />
    </Container>
  );
};

export default TechniciansList;
